import pick from "lodash/pick";
import moment from "moment";

const normalizeTitle = (t) => (t ?? "").trim().toLowerCase();

export const isItemFilled = (item) => {
  if (item?.title && Object.values(item?.days).includes(true)) return true;
  return false;
};

export const isStateFilled = (state) => {
  if (typeof state !== "object") return false;
  let filled = true;
  state.forEach((item) => {
    if (!isItemFilled(item)) filled = false;
  });
  return filled;
};

export const compareArrays = (array1, array2) => {
  if (typeof array1 !== "object" || typeof array2 !== "object") return false;
  if (array1.length !== array2.length) return true;
  let different = false;
  array1.forEach((item, i) => {
    if (!different) {
      if (item.title !== array2[i].title) different = true;
      if (item.description !== array2[i].description) different = true;
      const equal = shallowEqual(item.days, array2[i].days);
      if (!equal) different = true;
    }
  });
  return different;
};

export function shallowEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (object1[key] !== object2[key]) {
      return false;
    }
  }
  return true;
}
export const filterAccountabilityAttrs = (acc) =>
  pick(acc, ["_id", "title", "description", "days"]);

// Matches Profile → Tasks schema and mobile dashboard (sun-first, 3-letter keys).
const WEEKDAY_ABBREVS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/** Weekday abbrev for a calendar dayKey; uses UTC to match the plan month grid. */
export const getDayAbbrevFromDayKey = (dayKey) => {
  if (!dayKey) return null;
  return WEEKDAY_ABBREVS[moment.utc(dayKey, "YYYY-MM-DD").day()];
};

export const getHabitsForDay = (habits, dayKey) => {
  if (!habits?.length || !dayKey) return [];
  const dayAbbrev = getDayAbbrevFromDayKey(dayKey);
  if (!dayAbbrev) return [];
  return habits.filter((h) => h.days?.[dayAbbrev]);
};

/** client-configuration `tracking.items` keys rendered as calendar "tracking" cards. */
export const TRACKING_CARD_TRACKER_KEYS = [
  "readiness",
  "bodyWeight",
  "bodyMeasurements",
  "progressPhotos",
  "mealPhotos",
];

/** `coachRequired || clientEnabled` — mirrors msb-mobile's `isTrackerEffectivelyOn`. */
const isTrackerEffectivelyOn = (item) =>
  item?.coachRequired === true || item?.clientEnabled === true;

/** Weekday index used by a tracker's `schedule` array: 0=Sun … 6=Sat, per
 * client-configuration's convention (matches `Date#getDay`). */
const getScheduleWeekdayIndexFromDayKey = (dayKey) =>
  moment.utc(dayKey, "YYYY-MM-DD").day();

/** Empty/absent `schedule` means "every day" — matches msb-mobile's `isTrackerScheduledOnDate`. */
const isTrackerScheduledOnWeekday = (item, weekdayIndex) => {
  const schedule = Array.isArray(item?.schedule) ? item.schedule : [];
  return schedule.length === 0 || schedule.includes(weekdayIndex);
};

/**
 * @param {object} trackingItems - `clientConfiguration.sections.tracking.items`
 * @param {string} dayKey
 * @param {{ hasTrainingOnDay?: boolean }} [options] - `readiness` is
 *   training-gated (no `schedule` of its own), so it only counts on days
 *   that actually have training, per `options.hasTrainingOnDay`.
 */
export const getTrackingForDay = (trackingItems, dayKey, options = {}) => {
  if (!trackingItems || !dayKey) return {};
  const weekdayIndex = getScheduleWeekdayIndexFromDayKey(dayKey);
  const result = {};
  for (const key of TRACKING_CARD_TRACKER_KEYS) {
    const item = trackingItems[key];
    if (!isTrackerEffectivelyOn(item)) continue;
    if (key === "readiness") {
      if (options.hasTrainingOnDay) result[key] = true;
    } else if (isTrackerScheduledOnWeekday(item, weekdayIndex)) {
      result[key] = true;
    }
  }
  return result;
};

export const hasTrackingForDay = (trackingItems, dayKey, options = {}) =>
  Object.keys(getTrackingForDay(trackingItems, dayKey, options)).length > 0;

export const getAccountabilitiesList = (coach, athlete) => {
  //if the athlete has a template assigned, I looked for it on the coach accountability templates and return it,
  if (athlete.accountability?.templateId) {
    const assignedTemplate = coach?.accountabilityTemplates?.templates?.find(
      (template) => template._id === athlete.accountability.templateId
    );
    if (assignedTemplate) return assignedTemplate.list;
  }
  //if there isn't template assigned, I check if the athlete has a list of accountabilities and return it
  if (athlete.accountability?.accountabilities) {
    return athlete.accountability?.accountabilities;
  }
  // In any other case, I return the default template assigned by the coach
  if (coach?.accountabilityTemplates?.defaultTemplate) {
    const assignedTemplate = coach?.accountabilityTemplates?.templates?.find(
      (template) =>
        template._id === coach.accountabilityTemplates.defaultTemplate
    );
    if (assignedTemplate) return assignedTemplate.list;
  }
  //If the coach hasnt selected a default template yet, an empty array is returned
  return [];
};
export const isEditing = (state) => {
  let editing = false;
  state && state.forEach((item) => item.editing && (editing = true));
  return editing;
};

export const getAccountabilitiesTable = (
  dailyStats,
  date,
  assignments,
  templates,
  defaultAssignments,
  weeklyData,
  athleteSignupDate,
  userAccountability
) => {
  const momentDate = moment(date, "YYYYMMDD");
  const range = weeklyData ? "week" : "month";
  const endOfRange = Number(moment(momentDate).endOf(range).format("YYYYMMDD"));

  const startOfRange = Number(
    moment(momentDate).startOf(range).format("YYYYMMDD")
  );

  //get the records where the athelete saved accountabilities
  const rangeStats = dailyStats.filter(
    (stat) =>
      stat.accountabilities &&
      stat.utcDate >= startOfRange &&
      stat.utcDate <= endOfRange
  );
  const userAssignments = userAccountability
    ? [userAccountability]
    : assignments;
  //get the month assignments
  const rangeAssignments = userAssignments?.filter(
    (assignment) =>
      //the range started before the end of the month and havent ended
      (assignment.startDate <= endOfRange && !assignment.endDate) ||
      //the range started before the start of the month and matches at least a few days of the month
      (assignment.startDate < startOfRange &&
        assignment.endDate > startOfRange) ||
      //the range is included in the month
      (assignment.startDate >= startOfRange &&
        assignment.startDate <= endOfRange)
  );
  //add template accountabilities if there is a range with templateId
  const assignmentsWithTemplates = rangeAssignments?.map((item) => {
    if (item.assignmentType === "accountability" || userAccountability)
      return item;
    else {
      const template = templates?.find(
        (element) => element._id === item.templateId
      );
      return template ? { ...item, accountabilities: template.list } : item;
    }
  });
  //Evidence-based inference: an athlete-specific assignment with no endDate is
  //otherwise treated as active forever, permanently shadowing the coach's
  //defaultAssignments even after the athlete has clearly moved on (e.g. a coach
  //removed the athlete's override but the historical entry was never closed —
  //see the daily-accountability.js DELETE handler fix). If the athlete's own
  //logged completions show a sustained switch to items this entry doesn't
  //cover, treat the entry as ending at the close of that month — in memory
  //only, `assignments`/`dailyStats` are never written back.
  const DIVERGENCE_THRESHOLD = 3; // consecutive logged days with zero overlap before trusting a switch
  const inferEffectiveEndDate = (entry) => {
    const keys = new Set();
    entry.accountabilities?.forEach((item) => {
      keys.add(String(item._id));
      const t = normalizeTitle(item.title);
      if (t) keys.add(t);
    });
    if (!keys.size) return null;

    const relevantStats = dailyStats
      .filter(
        (s) =>
          s.accountabilities?.length && s.utcDate >= Number(entry.startDate)
      )
      .sort((a, b) => a.utcDate - b.utcDate);

    let divergenceRun = [];
    for (const stat of relevantStats) {
      const matches = stat.accountabilities.some(
        (acc) =>
          keys.has(String(acc._id)) || keys.has(normalizeTitle(acc.title))
      );
      if (matches) {
        divergenceRun = [];
      } else {
        divergenceRun.push(stat);
        if (divergenceRun.length >= DIVERGENCE_THRESHOLD) {
          //Month-level cutover: the entry stays active through the end of the
          //month the switch is detected in, even for days already logged
          //against the default within that same month — the default only
          //takes over starting the following month, matching the Tracker's
          //own monthly grain.
          return moment(String(divergenceRun[0].utcDate), "YYYYMMDD")
            .endOf("month")
            .format("YYYYMMDD");
        }
      }
    }
    return null;
  };
  const assignmentsWithInferredEnds = userAccountability
    ? assignmentsWithTemplates
    : assignmentsWithTemplates.map((entry) =>
        entry.endDate
          ? entry
          : {
              ...entry,
              endDate: inferEffectiveEndDate(entry) ?? entry.endDate,
            }
      );
  const rangeArray = weeklyData
    ? new Array(7).fill(false)
    : new Array(moment(momentDate).daysInMonth()).fill(false);
  const rangeStartDate = moment(momentDate).startOf(range);
  const days = rangeArray.map((day, i) => {
    const currentDay = moment(rangeStartDate).add(i, "days");
    return {
      key: currentDay.day(),
      dayNumber: currentDay.date(),
      utcDate: moment(currentDay).format("YYYYMMDD"),
      found: false,
    };
  });
  const rows = {};

  const rowValues = rangeArray.map((item) => {
    return { assigned: false, done: false };
  });
  //make a list of assigned accountabilities
  assignmentsWithInferredEnds.forEach((list) => {
    let isInAssignmentRange = false;
    days.forEach((day, i) => {
      if (
        Number(list.startDate) <= day.utcDate &&
        (!list.endDate || day.utcDate <= Number(list.endDate))
      ) {
        isInAssignmentRange = true;
        days[i] = { ...days[i], found: true };
      }
      if (isInAssignmentRange)
        list.accountabilities?.forEach((item) => {
          rows[item._id]
            ? null
            : (rows[item._id] = {
                title: item.title,
                days: rowValues,
              });
        });
    });
  });
  defaultAssignments?.forEach((assignment) => {
    let isInTheRange = false;
    days.forEach((day, i) => {
      if (
        Number(day.utcDate) >= Number(athleteSignupDate) &&
        !day.found &&
        Number(assignment.startDate) <= day.utcDate &&
        (!assignment.endDate || day.utcDate <= Number(assignment.endDate))
      ) {
        isInTheRange = true;
      }
      if (isInTheRange) {
        assignment.accountabilities?.forEach((item) => {
          rows[item._id]
            ? null
            : (rows[item._id] = {
                title: item.title,
                days: rowValues,
              });
        });
      }
    });
  });
  let assignedAccountabilityRows = {};
  Object.keys(rows).forEach((accountabilityId) => {
    assignedAccountabilityRows = {
      ...assignedAccountabilityRows,
      [accountabilityId]: {
        ...rows[accountabilityId],
        days: days.map((day) =>
          isAccountabilityAssigned(
            assignmentsWithInferredEnds,
            accountabilityId,
            day.utcDate,
            defaultAssignments,
            athleteSignupDate
          )
            ? { assigned: true, done: false }
            : { assigned: false, done: false }
        ),
      },
    };
  });
  //Index the assigned (config-derived) rows by normalized title so a stored
  //completion whose _id drifted from the configured _id can still be matched
  //back to its row by title. First assigned row wins on duplicate titles.
  const rowKeyByTitle = {};
  Object.keys(assignedAccountabilityRows).forEach((key) => {
    const t = normalizeTitle(assignedAccountabilityRows[key].title);
    if (t && !(t in rowKeyByTitle)) rowKeyByTitle[t] = key;
  });
  //add default items if there are any that matches the dates range
  //getAssignments for each day in the month
  if (rangeStats.length > 0) {
    //A row is a line in the table, each one has a key (which is the accountability id) and a value (props to make the row values=> false or true)

    rangeStats.forEach((day) => {
      const dayOfTheRange = moment(day.utcDate).diff(startOfRange);
      day.accountabilities.forEach((acc) => {
        //Match the stored completion to an assigned row by _id first, then fall
        //back to the normalized title (handles historical _id drift), and finally
        //fall back to the stored _id itself so a completion is never silently
        //dropped even when it belongs to a template/list that the current
        //assignment history doesn't recognize as active for that day.
        const targetKey =
          (assignedAccountabilityRows[acc._id] ? acc._id : null) ??
          rowKeyByTitle[normalizeTitle(acc.title)] ??
          acc._id;
        const row = assignedAccountabilityRows[targetKey];
        const baseDays = row?.days ?? rowValues;
        assignedAccountabilityRows[targetKey] = {
          title: acc.title,
          ...row,
          days: baseDays.map((item, i) =>
            i == dayOfTheRange
              ? {
                  //Trust the athlete's own stored completion: if they logged this
                  //item that day, count the day as assigned even if the current
                  //assignment history no longer/never recognized that exact
                  //id/title (matches msb-api's getAccountabilitiesTable, used by
                  //the weekly-accountability endpoint).
                  assigned: true,
                  done: item.done || acc.done,
                }
              : item
          ),
        };
      });
    });
    let dataToReturn = {
      days,
      rows: Object.keys(assignedAccountabilityRows)
        .map((key) => {
          return {
            ...assignedAccountabilityRows[key],
            _id: key,
          };
        })
        .filter((row) => {
          let flag = false;
          row.days.forEach((day) => (day.assigned ? (flag = true) : null));
          return flag;
        }),
    };
    //add percentage of adherence values
    dataToReturn = {
      ...dataToReturn,
      rowAdherenceValues: dataToReturn.rows.map((row) =>
        getAdherencePercentage(row.days)
      ),
    };
    //add average adherence
    dataToReturn = {
      ...dataToReturn,
      averageAdherence: getAverageAdherence(dataToReturn.rowAdherenceValues),
    };
    return dataToReturn;
  }

  let dataToReturn = {
    days,
    rows: Object.keys(assignedAccountabilityRows)
      .map((key) => {
        return {
          ...assignedAccountabilityRows[key],
          _id: key,
          days: assignedAccountabilityRows[key].days,
        };
      })
      .filter((row) => {
        let flag = false;
        row.days.forEach((day) => (day.assigned ? (flag = true) : null));
        return flag;
      }),
  };
  //add percentage of adherence values
  dataToReturn = {
    ...dataToReturn,
    rowAdherenceValues: dataToReturn.rows.map((row) =>
      getAdherencePercentage(row.days)
    ),
  };
  //add average adherence
  dataToReturn = {
    ...dataToReturn,
    averageAdherence: getAverageAdherence(dataToReturn.rowAdherenceValues),
  };
  return dataToReturn;
};

const isAccountabilityAssigned = (
  assignmentsWithTemplates, //list of athlete assignations of accountabilities
  accountabilitId,
  utcDate, //the current date
  defaultAssignments, //list of default assignments by the coach
  athleteSignupDate
) => {
  const currentDay = moment().utc().format("YYYYMMDD");

  let assigned = false;
  if (Number(currentDay) < Number(utcDate)) return assigned;
  let isInAssignmentWithTemplatesRange = false;
  assignmentsWithTemplates.forEach((list) => {
    let isInTheRange = false;
    if (
      Number(list.startDate) <= Number(utcDate) &&
      (!list.endDate || Number(utcDate) <= Number(list.endDate))
    ) {
      isInAssignmentWithTemplatesRange = true;
      isInTheRange = true;
    }
    if (isInTheRange)
      list.accountabilities?.forEach((item) => {
        if (item._id == accountabilitId) {
          if (
            item.days[moment(utcDate, "YYYYMMDD").format("ddd").toLowerCase()]
          )
            assigned = true;
        }
      });
  });

  if (!assigned && !isInAssignmentWithTemplatesRange) {
    defaultAssignments?.forEach((assignment) => {
      let isInTheRange = false;
      if (
        Number(utcDate) >= Number(athleteSignupDate) &&
        Number(assignment.startDate) <= utcDate &&
        (!assignment.endDate || utcDate <= Number(assignment.endDate))
      )
        isInTheRange = true;
      if (isInTheRange) {
        assignment.accountabilities?.forEach((item) => {
          if (item._id === accountabilitId) {
            if (
              item.days[moment(utcDate, "YYYYMMDD").format("ddd").toLowerCase()]
            )
              assigned = true;
          }
        });
      }
    });
  }
  return assigned;
};
export const getAdherencePercentage = (rowvalues) => {
  let daysAssigned = 0;
  let doneDays = 0;
  rowvalues.forEach((item) => {
    item.assigned && daysAssigned++;
    item.assigned && item.done && doneDays++;
  });
  if (doneDays === 0) return 0;
  if (daysAssigned === 0) return 0;

  return Math.round((doneDays / daysAssigned) * 100);
};

export const getAverageAdherence = (percentages) => {
  let sum = 0;
  if (percentages.length === 0) return 0;
  percentages.forEach((item) => (sum = sum + item));
  return sum / percentages.length;
};
